import { useEffect, type ReactNode } from 'react';
import { Spin } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useAuthUser,
  usePortalPermissions,
  viewPermissionForPath,
  parseUserRole,
  signOut,
  type UserRole,
} from '@ubus/shared';
import ForbiddenPage from '../pages/ForbiddenPage';

export interface PermissionRouteProps {
  /** JWT identity roles allowed (candidate set). */
  allowIdentity: UserRole[];
  /** Optional explicit view permission; otherwise derived from path. */
  permission?: string;
  children: ReactNode;
}

/**
 * Dual gate: JWT identity ∩ portal permission view code.
 * Disabled profiles are signed out.
 */
export function PermissionRoute({
  allowIdentity,
  permission,
  children,
}: PermissionRouteProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthUser();
  const identity = parseUserRole(user);
  const { access, hasPermission, loading } = usePortalPermissions();

  useEffect(() => {
    if (loading) return;
    if (access?.status === 'disabled') {
      void signOut().then(() => navigate('/login', { replace: true }));
    }
  }, [access?.status, loading, navigate]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (!identity || !allowIdentity.includes(identity)) {
    return <ForbiddenPage />;
  }

  const required =
    permission ?? viewPermissionForPath(location.pathname) ?? null;

  if (required && !hasPermission(required)) {
    return <ForbiddenPage />;
  }

  if (access?.status === 'disabled') {
    return null;
  }

  return <>{children}</>;
}

export default PermissionRoute;
